/**
 * tco-date.js
 *
 * Datele și valorile implicite ale calculatorului TCO auto.
 * SEPARAT de logică, exact ca să poți actualiza prețurile fără să atingi formulele.
 *
 * RITUAL DE ACTUALIZARE (când se schimbă prețurile la energie sau carburanți):
 *   1. schimbi valorile de mai jos
 *   2. actualizezi ACTUALIZAT_LA
 *   3. rulezi:  node tools/build-ssr.mjs
 *   4. dai push pe tema nepublicată și verifici cu curl
 *
 * Pasul 3 e obligatoriu. Fără el, tabelul din HTML rămâne pe cifrele vechi.
 *
 * Legendă încredere:
 *   [PRODUCTIE] valoare preluată 1:1 din calculatorul LIVE de pe buhnici.ro/tco
 *               (extrase în brief-ul de portare, iulie 2026)
 *   [PIATA]     preț de piață observat, de reverificat periodic
 *   [REPER]     estimare rezonabilă, medie pentru România
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory();
  } else {
    root.TCODate = factory();
  }
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  var ACTUALIZAT_LA = 'iulie 2026';

  /**
   * Ipotezele globale implicite. [PRODUCTIE] toate, din state-ul WordPress.
   */
  var IPOTEZE = {
    kmAn: 15000,     // km pe an, media unui șofer activ din România
    ani: 5,          // ani de deținere
    eur: 5,          // curs EUR -> RON (în interfață: min 3, max 8, pas 0.05)
    prosumer: false  // încărcare solară oprită implicit
  };

  /**
   * Cele 6 preseturi de mașini. [PRODUCTIE] toate valorile, preluate 1:1
   * din calculatorul live buhnici.ro/tco (brief de portare, iulie 2026).
   *
   * Semnificația câmpurilor din p:
   *   pretAchizitie, pretRevanzare  EUR (se convertesc în RON prin cursul eur)
   *   consum                        kWh/100km la electric, l/100km la termice
   *   pretEnergie                   RON/kWh la electric, RON/l la termice [PIATA]
   *   consumElectric, pretElectric  partea electrică a PHEV (kWh/100km, RON/kWh)
   *   ratioElectric                 % din km parcurși pe electric (PHEV)
   *   rcaAnual, cascoAnual          RON/an [PIATA]
   *   rovinieta                     impozit + rovinietă, RON/an
   *   serviceAnual                  service + ITP, RON/an [REPER]
   *   anvelope5ani                  cost TOTAL anvelope pe 5 ani, RON [REPER]
   *   altele                        spălat, parcare, RON/an [REPER]
   */
  var PRESETS = [
    { id: 'ev_mic', name: 'EV mic', sub: 'tip Dacia Spring', type: 'electric', color: '#2bd576',
      p: { pretAchizitie: 18000, pretRevanzare: 7500, consum: 14, pretEnergie: 1.2,
           consumElectric: 14, pretElectric: 1.2, ratioElectric: 100,
           rcaAnual: 1200, cascoAnual: 0, rovinieta: 0, serviceAnual: 500, anvelope5ani: 3000, altele: 1200 } },

    { id: 'ev_prem', name: 'EV premium', sub: 'tip Tesla Model Y', type: 'electric', color: '#ff5d5d',
      p: { pretAchizitie: 39000, pretRevanzare: 25000, consum: 16, pretEnergie: 1.2,
           consumElectric: 16, pretElectric: 1.2, ratioElectric: 100,
           rcaAnual: 1800, cascoAnual: 5000, rovinieta: 0, serviceAnual: 1000, anvelope5ani: 8000, altele: 2400 } },

    // HEV este modelat ca benzină: arde doar benzină, doar că mai puțină.
    { id: 'hev', name: 'Hibrid (HEV)', sub: 'tip Toyota Corolla', type: 'benzina', color: '#19b3ff',
      p: { pretAchizitie: 22000, pretRevanzare: 14000, consum: 4.5, pretEnergie: 7.5,
           consumElectric: 0, pretElectric: 1.2, ratioElectric: 0,
           rcaAnual: 1500, cascoAnual: 0, rovinieta: 400, serviceAnual: 1500, anvelope5ani: 4000, altele: 1200 } },

    { id: 'benzina', name: 'Benzină', sub: 'tip Skoda Octavia 1.5', type: 'benzina', color: '#34d399',
      p: { pretAchizitie: 23000, pretRevanzare: 12500, consum: 6.8, pretEnergie: 7.5,
           consumElectric: 0, pretElectric: 1.2, ratioElectric: 0,
           rcaAnual: 1500, cascoAnual: 0, rovinieta: 400, serviceAnual: 1800, anvelope5ani: 4000, altele: 1200 } },

    { id: 'phev', name: 'PHEV SUV', sub: 'tip Hyundai Tucson PHEV', type: 'phev', color: '#8b5cf6',
      p: { pretAchizitie: 45000, pretRevanzare: 22500, consum: 6.5, pretEnergie: 7.5,
           consumElectric: 18, pretElectric: 1.2, ratioElectric: 40,
           rcaAnual: 2000, cascoAnual: 5000, rovinieta: 0, serviceAnual: 2500, anvelope5ani: 8000, altele: 2400 } },

    { id: 'diesel', name: 'Diesel SH', sub: 'SUV 2018-2019', type: 'diesel', color: '#f59e0b',
      p: { pretAchizitie: 15000, pretRevanzare: 9000, consum: 7.5, pretEnergie: 7.8,
           consumElectric: 0, pretElectric: 1.2, ratioElectric: 0,
           rcaAnual: 1800, cascoAnual: 0, rovinieta: 600, serviceAnual: 4000, anvelope5ani: 5000, altele: 1200 } }
  ];

  /**
   * Texte de ajutor. [PRODUCTIE] preluate exact din tooltip-urile WordPress.
   */
  var TEXTE = {
    prosumer: 'Dacă ai panouri fotovoltaice și încarci mașina electrică din producția proprie, costul energiei electrice devine 0. Nu afectează benzina/motorina.',
    eur: 'Prețul de achiziție și revânzarea sunt în EUR (așa se listează mașinile). Le transformăm în RON cu acest curs.'
  };

  return {
    ACTUALIZAT_LA: ACTUALIZAT_LA,
    IPOTEZE: IPOTEZE,
    PRESETS: PRESETS,
    TEXTE: TEXTE
  };
});
